<script lang="ts">
	import { BlockType as BlockTypeEnum, CustomIcon as CustomIconEnum } from '$lib/graphql/enums'
	import type { BlockType, CustomColor, CustomIcon } from '$lib/graphql/types'
	import blockState from '$lib/stores/block-state'
	import { override } from '$lib/utils/classnames'
	import { getCustomColorBgClassName, getCustomColorTextClassName } from '$lib/utils/custom-colors'
	import { getCustomIconSvgUrl } from '$lib/utils/custom-icons'
	import { twMerge } from 'tailwind-merge'
	import PinIcon from '../icons/PinIcon.svelte'
	import { stopPropagation } from '$lib/utils/clickHelpers'
	import type { MediaFieldsFragment } from '$src/lib/queries/fragments/generated/MediaFields'
	import { constructCdnUrlThumbnail } from '$src/lib/utils/cloudflare-loader'

	interface Props {
		isActive?: boolean
		isHighlighted?: boolean
		shouldHighlight?: boolean
		isSearchMode?: boolean
		color?: CustomColor | null
		icon?: CustomIcon | null
		blockType?: BlockType | null
		blockId?: string | null
		featuredImage?: MediaFieldsFragment | null
		interact?: (blockId: string, interactionType: string | null, isSearch: boolean) => void
	}

	let {
		isActive = false,
		isHighlighted = false,
		shouldHighlight = false,
		isSearchMode = false,
		color = null,
		icon = null,
		blockType = null,
		blockId = null,
		featuredImage = null,
		interact,
	}: Props = $props()

	function handleImageError(event: Event) {
		const target = event.target as HTMLImageElement
		target.parentElement.appendChild(document.createElement('div'))
	}

	function handleMarkerInteraction(interactionType: string) {
		interact?.(blockId, interactionType, isSearchMode)
	}

	function handleOpenBlock() {
		interact?.(blockId, null, isSearchMode)
	}

	let isHovering = $state(false)

	$effect(() => {
		isHovering = $blockState.hoveringPlaceId === blockId
	})

	let colorClassName = $derived(getCustomColorBgClassName(color, blockType))
	let computedClassName = $derived(
		twMerge(
			override(
				'h-6 w-6 m-auto rounded-full flex items-center shadow-md justify-center',
				colorClassName,
			),
		),
	)

	let iconUrl = $derived(getCustomIconSvgUrl(icon, blockType))
</script>

{#if !blockType}
	<button
		data-testid="map-marker"
		onmouseenter={() => handleMarkerInteraction('mouseenter')}
		onmouseleave={() => handleMarkerInteraction('mouseleave')}
		onclick={stopPropagation(handleOpenBlock)}
		class={`scale-100 transition-all text-sm font-medium touch-manipulation ${
			isActive || isHovering
				? 'scale-125 -translate-y-1 opacity-100'
				: 'hover:scale-125 hover:-translate-y-1 opacity-85'
		}`}
	>
		<PinIcon class={`h-10 w-10 m-auto ${color == 'RED' ? 'text-red-500' : 'text-pink-500'}`} />
	</button>
{:else if blockType === BlockTypeEnum.Destination && !isSearchMode}
	<button
		data-testid="map-marker"
		onclick={stopPropagation(handleOpenBlock)}
		class="scale-100 transition-transform duration-200 text-sm font-medium translate-y-[15px] hover:scale-125 touch-manipulation"
		aria-label="Destination Pin"
	>
		{#if featuredImage}
			<img
				src={constructCdnUrlThumbnail(featuredImage.cloudflareID)}
				onerror={handleImageError}
				alt="featured"
				class="h-12 w-12 rounded-full border-2 border-pink-600/30 shadow-md z-10"
			/>
		{:else}
			<div
				class={'h-6 w-6 m-auto rounded-full flex items-center shadow-md justify-center bg-pink-500'}
			>
				<img src={'/visuals/icons/flag.svg'} alt="marker icon" class="h-3 w-3" />
			</div>
		{/if}
	</button>
{:else}
	<button
		data-testid="map-marker"
		onmouseenter={() => handleMarkerInteraction('mouseenter')}
		onmouseleave={() => handleMarkerInteraction('mouseleave')}
		onclick={stopPropagation(handleOpenBlock)}
		class={`
				scale-100 transition-all duration-200 text-sm font-medium touch-manipulation
				${isActive || isHovering ? 'z-40 scale-125 -translate-y-1' : 'hover:scale-125 hover:-translate-y-1'}
				${shouldHighlight ? (isHighlighted ? 'opacity-100' : 'opacity-25') : 'opacity-100'}
			`}
		aria-label="Custom Pin"
	>
		<div class={computedClassName}>
			<img src={iconUrl} alt="marker icon" class="h-3 w-3" />
		</div>
	</button>
{/if}

<style>
	@keyframes ripple {
		0% {
			transform: scale(0.9);
			opacity: 20%;
		}
		100% {
			transform: scale(2);
			opacity: 0%;
		}
	}

	:global(.animate-ripple) {
		animation: ripple 2s infinite;
	}

	@keyframes scale-animation {
		0%,
		100% {
			transform: scale(1);
		}
		50% {
			transform: scale(1.2);
		}
	}

	:global(.animate-scale) {
		animation: scale-animation 2s infinite;
	}
</style>
